
import { defineComponent, defineAsyncComponent, PropType } from "vue";
import type { ActionButtonOptions, ScoreElement } from "@/data/types";
import Countdown from "@/data/Countdown";
import ActionButton from "@/components/ActionButton.vue";
import ScoreView from "@/components/ScoreView.vue";
import TimeScoreView from "@/components/TimeScoreView.vue";
import { deviceType } from "@/helper/util";
import GameHelpModal from "@/components/GameHelpModal.vue";
import IconButton from "@/components/IconButton.vue";
import PlayButton from "@/components/PlayButton.vue";

export default defineComponent({
  name: "GameWrapper",

  components: {
    ActionButton,
    ScoreView,
    TimeScoreView,
    GameHelpModal,
    IconButton,
    PlayButton,
  },

  data() {
    return {
      preCountdown: new Countdown(3) as Countdown,
      preTimer: 0,
      isHelpModalOpen: false,
      showGameOver: false,
    };
  },

  props: {
    actionButtons: {
      type: Array as PropType<ActionButtonOptions[]>,
      default: () => [],
    },
    title: {
      type: String,
      default: "",
    },
    name: {
      type: String,
      default: "",
    },
    isGameOver: {
      type: Boolean,
      required: true,
    },
    counter: {
      type: Number,
      required: true,
    },
    scoreElements: {
      type: Array as PropType<ScoreElement[]>,
      default: () => [],
    },
    instruction: {
      type: String,
      default: "",
    },
    scoreTime: {
      type: Number,
      default: 0,
    },
    hasNoPreCountdown: {
      type: Boolean,
      default: false,
    },
  },

  beforeMount() {
    document.addEventListener("keydown", this.handleKeyDown, false);
  },

  beforeUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown, false);
  },

  mounted() {
    this.$store.commit("setGame", this.name);
    this.$store.dispatch("getHighscore", { name: this.name });
  },

  computed: {
    gameHelperComponent() {
      return defineAsyncComponent(
        () => import(`@/components/gameHelp/${this.name}Help.vue`)
      );
    },
    isPreCountdownRunning(): boolean {
      return this.preCountdown.isRunning;
    },
    isFirstGame(): boolean {
      return this.counter < 1;
    },
    hasTopSlotContent(): boolean {
      return !!this.$slots["top"];
    },
    isMobile(): boolean {
      return deviceType() !== "desktop";
    },
  },

  methods: {
    handleKeyDown(event: KeyboardEvent) {
      switch (event.code) {
        case "KeyB":
          event.preventDefault();
          this.$router.push("/");
          break;
        case "KeyR":
        case "KeyS":
          event.preventDefault();
          this.$emit("restart");
          break;
      }
      this.actionButtons.forEach((button) => {
        if (
          button.code &&
          (button.code === event.code || button.altCode === event.code)
        ) {
          event.preventDefault();
          this.$emit(button.clickEvent.event, button.clickEvent.value);
        }
      });
    },
    openModal() {
      this.isHelpModalOpen = true;
    },
    buttonSizeClass(button: ActionButtonOptions) {
      return `game-wrapper__action-button--${button.buttonSize}`;
    },
    routeToHome() {
      this.$store.commit("transitionName", "game-to-home");
      setTimeout(() => {
        this.$router.push("/");
      }, 1100);
    },
  },

  watch: {
    counter(newVal, oldVal) {
      if (newVal > oldVal && !this.hasNoPreCountdown) {
        this.preCountdown.reset();
        this.preCountdown.start();
      } else {
        this.$emit("precountdown-over");
      }
    },
    preCountdown: {
      deep: true,
      handler(newVal) {
        if (newVal.isOver) {
          this.$emit("precountdown-over");
        }
      },
    },
  },
});
